import { LegalDocumentLayout } from "./legal-document-layout";
import {
  BUSINESS_PLATFORM_FEE_DISCLOSURE,
  FACILITY_SERVICE_PRICE_DISCLOSURE,
  FACILITY_SERVICE_TRANSACTION_DISCLOSURE,
  HISTORICAL_TERMS_DISCLOSURE,
  NONBINDING_TERMS_DRAFT_NOTICE,
  PLAYER_CHECKOUT_FEE_DISCLOSURE,
  PLATFORM_LEGAL_DISPLAY_NAME,
  PLATFORM_LEGAL_NAME,
  SOFTWARE_MODULE_PAYMENT_DISCLOSURE,
  TERMS_CHANGE_DISCLOSURE,
  UNAPPROVED_DISPUTE_DISCLOSURE,
} from "@bw/shared";

/**
 * Public Terms of Service. Ported verbatim from business-v2
 * `src/app/terms/page.tsx` — same static legal text, same structure. The only
 * legal copy. The shared MUI legal-document layout owns the public page chrome,
 * responsive measure, and typography; document titles remain semantic h1s.
 *
 * Rendered OUTSIDE the dashboard/auth shell (public, no RequireAuth).
 */
export function TermsPage() {
  return (
    <LegalDocumentLayout title="Terms of Service">
      <p>
        Welcome to Badminton Warriors. These Terms of Service
        (&quot;Terms&quot;) govern your use of the Badminton Warriors platform,
        website, and mobile applications (collectively, the
        &quot;Platform&quot;) operated by {PLATFORM_LEGAL_DISPLAY_NAME}
        (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
      </p>
      <p>{NONBINDING_TERMS_DRAFT_NOTICE}</p>

      <h2>1. Platform Description and Role</h2>
      <p>
        Badminton Warriors is a software platform that connects badminton
        players with facility operators. We provide tools for court booking,
        community engagement, player rankings, coaching management, and facility
        operations.
      </p>
      <p>
        We act solely as an intermediary. We are not a badminton facility
        operator, sports venue, coaching provider, or event organizer. The
        facilities listed on the Platform are independently owned and operated
        businesses. We do not control and are not responsible for the quality,
        safety, or legality of the services provided by facility operators.
      </p>

      <h2>2. User Accounts and Responsibilities</h2>
      <p>
        To use the Platform, you must create an account and provide accurate,
        complete information. You are responsible for:
      </p>
      <ul>
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activity that occurs under your account</li>
        <li>Keeping your account information current and accurate</li>
        <li>
          Notifying us immediately of any unauthorized use of your account
        </li>
      </ul>
      <p>
        If you are under 13, your account remains restricted until the Platform
        records verified consent from a parent or legal guardian. Other minor
        participation may require feature-specific guardian review. These
        controls do not replace a facility&apos;s own eligibility rules.
      </p>
      <p>
        You agree to use the Platform only for lawful purposes and in accordance
        with these Terms. You must not use the Platform to engage in harassment,
        distribute harmful content, impersonate others, or interfere with the
        Platform&apos;s operation.
      </p>

      <h2>3. Facility Services and Payments</h2>
      <p>{FACILITY_SERVICE_TRANSACTION_DISCLOSURE}</p>
      <p>
        Facility-service payment processing is provided by Stripe, Inc. through
        its Stripe Connect platform. By making that payment through the Platform,
        you agree to Stripe&apos;s{" "}
        <a
          href="https://stripe.com/legal/connect-account"
          target="_blank"
          rel="noopener noreferrer"
        >
          Connected Account Agreement
        </a>{" "}
        and{" "}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        .
      </p>
      <p>{FACILITY_SERVICE_PRICE_DISCLOSURE}</p>

      <h2>4. Platform and Software Fees</h2>
      <p>{PLAYER_CHECKOUT_FEE_DISCLOSURE}</p>
      <p>{BUSINESS_PLATFORM_FEE_DISCLOSURE}</p>
      <p>{SOFTWARE_MODULE_PAYMENT_DISCLOSURE}</p>
      <p>{HISTORICAL_TERMS_DISCLOSURE}</p>

      <h2>5. Cancellation and Refund Policy</h2>
      <p>
        Cancellation and refund policies are set by each individual facility
        operator. The applicable cancellation policy is displayed before you
        confirm a booking. We strongly encourage you to review the cancellation
        policy before completing your reservation.
      </p>
      <p>
        Refunds, when applicable under a facility&apos;s policy, are processed
        through Stripe and returned to your original payment method. Processing
        times depend on your payment provider and may take 5 to 10 business
        days.
      </p>
      <p>
        If you believe a facility has not honored its stated cancellation
        policy, you may contact us at{" "}
        <a href="mailto:support@badmintonwarriors.com">
          support@badmintonwarriors.com
        </a>{" "}
        and we will work to facilitate a resolution. However, final refund
        decisions rest with the facility operator as the merchant of record.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>
        The Platform, including its design, logos, text, graphics, software, and
        all other content created by us, is protected by copyright, trademark,
        and other intellectual property laws. You may not copy, modify,
        distribute, sell, or create derivative works based on the Platform
        without our prior written consent.
      </p>
      <p>
        By posting content to the Platform (such as community posts, photos, or
        reviews), you grant us a non-exclusive, royalty-free, worldwide license
        to use, display, and distribute that content in connection with
        operating the Platform. You retain ownership of your content and may
        delete it at any time.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, {PLATFORM_LEGAL_NAME} and its
        officers, directors, employees, and agents shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages
        arising from your use of the Platform, including but not limited to:
      </p>
      <ul>
        <li>Injuries sustained during physical activities at any facility</li>
        <li>Disputes between players and facility operators</li>
        <li>Loss of data or unauthorized access to your account</li>
        <li>Service interruptions, errors, or inaccuracies on the Platform</li>
        <li>
          Actions or omissions of facility operators, coaches, or other users
        </li>
      </ul>
      <p>
        Our total liability for any claim arising from your use of the Platform
        shall not exceed the amount you paid through the Platform in the 12
        months preceding the claim.
      </p>
      <p>
        Badminton is a physical sport with inherent risks. By using the Platform
        to book courts or join activities, you acknowledge these risks and agree
        that we bear no responsibility for physical injuries or property damage
        incurred at any facility.
      </p>

      <h2>8. Dispute Resolution</h2>
      <p>{UNAPPROVED_DISPUTE_DISCLOSURE}</p>

      <h2>9. Termination</h2>
      <p>
        You may terminate your account at any time by contacting us at{" "}
        <a href="mailto:support@badmintonwarriors.com">
          support@badmintonwarriors.com
        </a>{" "}
        or through the Settings page in the app.
      </p>
      <p>
        We may suspend or terminate your account if you violate these Terms,
        engage in fraudulent activity, or behave in a manner that is harmful to
        other users, facility operators, or the Platform. We will provide notice
        before termination when reasonably possible.
      </p>
      <p>
        Upon termination, your right to use the Platform ceases immediately.
        Any provisions that are approved and by their nature should survive
        termination will be identified in the authorized version.
      </p>

      <h2>10. Changes to These Terms</h2>
      <p>{TERMS_CHANGE_DISCLOSURE}</p>

      <h2>11. Contact Us</h2>
      <p>If you have questions about these Terms, please contact us at:</p>
      <p>
        {PLATFORM_LEGAL_DISPLAY_NAME}
        <br />
        Email:{" "}
        <a href="mailto:legal@badmintonwarriors.com">
          legal@badmintonwarriors.com
        </a>
      </p>
    </LegalDocumentLayout>
  );
}
